/**
 * Перехват предложения установить приложение.
 *
 * Chrome бросает beforeinstallprompt ровно один раз за загрузку страницы и,
 * если preventDefault не вызван, показывает свою плашку. Событие не «залипает»:
 * слушатель, поднявшийся после того, как оно уже улетело, не получит ничего.
 *
 * Раньше слушатель жил в InstallPrompt внутри AppShell — то есть в ленивой
 * ветке /lk. Пользователь, открывший лендинг, терял событие безвозвратно:
 * к моменту, когда он доходил до кабинета, дозваться до него было уже нечем,
 * а клиентская навигация новой загрузки страницы не вызывает.
 *
 * Поэтому подписка живёт здесь и выполняется при импорте модуля из main.tsx —
 * до первого рендера. Событие складывается в модульную переменную, а компонент
 * забирает его когда угодно позже.
 */

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredEvent: BeforeInstallPromptEvent | null = null
const listeners = new Set<(event: BeforeInstallPromptEvent | null) => void>()

function notify() {
  for (const listener of listeners) listener(deferredEvent)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Без preventDefault Chrome покажет свою мини-плашку поверх нашей
    event.preventDefault()
    deferredEvent = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferredEvent = null
    notify()
  })
}

/** Событие, если оно уже приходило. */
export function getInstallEvent(): BeforeInstallPromptEvent | null {
  return deferredEvent
}

/**
 * Подписка на появление события. Возвращает функцию отписки.
 * Слушателя зовём сразу: событие могло прийти до монтирования компонента.
 */
export function subscribeInstallEvent(
  listener: (event: BeforeInstallPromptEvent | null) => void
): () => void {
  listeners.add(listener)
  listener(deferredEvent)
  return () => {
    listeners.delete(listener)
  }
}

/** Событие одноразовое: после prompt() второй раз его показать нельзя. */
export function consumeInstallEvent() {
  deferredEvent = null
  notify()
}
